import React from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';

type TextVariant = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'body1' | 'body2' | 'caption' | 'overline';
type TextAlign = 'left' | 'center' | 'right' | 'justify';
type FontWeight = 'normal' | 'medium' | 'bold';
type TextColor = 'primary' | 'secondary' | 'text' | 'lightText' | 'white' | 'black' | 'accent' | 'error' | 'success' | 'warning' | 'info';

interface TypographyProps {
  variant?: TextVariant;
  align?: TextAlign;
  weight?: FontWeight;
  color?: TextColor;
  noWrap?: boolean;
  gutterBottom?: boolean;
  className?: string;
  children: React.ReactNode;
  dangerouslySetInnerHTML?: {
    __html: string;
  };
}

const getFontSize = (variant: TextVariant) => {
  switch (variant) {
    case 'h1':
      return theme.fontSizes['5xl'];
    case 'h2':
      return theme.fontSizes['4xl'];
    case 'h3':
      return theme.fontSizes['3xl'];
    case 'h4':
      return theme.fontSizes['2xl'];
    case 'h5':
      return theme.fontSizes.xl;
    case 'h6':
      return theme.fontSizes.lg;
    case 'body1':
      return theme.fontSizes.md;
    case 'body2':
      return theme.fontSizes.sm;
    case 'caption':
      return theme.fontSizes.xs;
    case 'overline':
      return theme.fontSizes.xs;
    default:
      return theme.fontSizes.md;
  }
};

const getLineHeight = (variant: TextVariant) => {
  switch (variant) {
    case 'h1':
    case 'h2':
    case 'h3':
      return theme.lineHeights.tight;
    case 'h4':
    case 'h5':
    case 'h6':
      return theme.lineHeights.normal;
    case 'body1':
    case 'body2':
      return theme.lineHeights.normal;
    case 'caption':
    case 'overline':
      return theme.lineHeights.normal;
    default:
      return theme.lineHeights.normal;
  }
};

const getFontWeight = (weight: FontWeight) => {
  switch (weight) {
    case 'normal':
      return theme.fontWeights.normal;
    case 'medium':
      return theme.fontWeights.medium;
    case 'bold':
      return theme.fontWeights.bold;
    default:
      return theme.fontWeights.normal;
  }
};

const getTextColor = (color: TextColor) => {
  switch (color) {
    case 'primary':
      return theme.colors.primary;
    case 'secondary':
      return theme.colors.secondary;
    case 'text':
      return theme.colors.text;
    case 'lightText':
      return theme.colors.lightText;
    case 'white':
      return theme.colors.white;
    case 'black':
      return theme.colors.black;
    case 'accent':
      return theme.colors.accent;
    case 'error':
      return theme.colors.error;
    case 'success':
      return theme.colors.success;
    case 'warning':
      return theme.colors.warning;
    case 'info':
      return theme.colors.info;
    default:
      return theme.colors.text;
  }
};

const getComponent = (variant: TextVariant) => {
  switch (variant) {
    case 'h1':
      return 'h1';
    case 'h2':
      return 'h2';
    case 'h3':
      return 'h3';
    case 'h4':
      return 'h4';
    case 'h5':
      return 'h5';
    case 'h6':
      return 'h6';
    case 'body1':
    case 'body2':
      return 'p';
    case 'caption':
      return 'span';
    case 'overline':
      return 'span';
    default:
      return 'p';
  }
};

const StyledTypography = styled.div<{
  variant: TextVariant;
  align: TextAlign;
  weight: FontWeight;
  color: TextColor;
  noWrap: boolean;
  gutterBottom: boolean;
}>`
  font-size: ${props => getFontSize(props.variant)};
  line-height: ${props => getLineHeight(props.variant)};
  font-weight: ${props => getFontWeight(props.weight)};
  color: ${props => getTextColor(props.color)};
  text-align: ${props => props.align};
  margin-bottom: ${props => props.gutterBottom ? theme.space.md : '0'};
  
  ${props => props.noWrap && `
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `}
  
  ${props => props.variant === 'overline' && `
    text-transform: uppercase;
    letter-spacing: 1px;
  `}
`;

export const Typography: React.FC<TypographyProps> = ({
  variant = 'body1',
  align = 'left',
  weight = 'normal',
  color = 'text',
  noWrap = false,
  gutterBottom = false,
  className,
  children,
  dangerouslySetInnerHTML,
}) => {
  const Component = getComponent(variant);
  
  return (
    <StyledTypography
      as={Component}
      variant={variant}
      align={align}
      weight={weight}
      color={color}
      noWrap={noWrap}
      gutterBottom={gutterBottom}
      className={className}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
    >
      {!dangerouslySetInnerHTML && children}
    </StyledTypography>
  );
};

export default Typography; 